import React from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useParams } from '@common/hooks';
import { Button, Col, Row, Typography } from 'antd';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ApplicationProfile } from 'works/pages/organization/applications/application-profile';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Link,
} from '@common/components';
import { SubmissionsPausedDropdown } from '@applicant-flow/components';
import { StaffingRequestBar } from 'works/components/staffing-request-bar';
import { CandidateTabs } from './candidate-tabs';
import { OfferForm } from 'works/components/offer-form';
import { ApprovalChainDecisionSteps } from '@approvals/components';
import { HistoryInfoFragment } from '@common/graphql';
import { OFFER_INFO_DETAILS_FRAGMENT } from '@applicant-flow/graphql/fragments';
import { ApprovalChainContentFragments } from '@approvals/graphql/fragments';
import { buildDialogUrl } from '@common/services';

const { Title } = Typography;

const STAFFING_REQUEST_NAME = gql`
  query StaffingRequestPageQuery($id: ID!) {
    staffingRequest(id: $id) {
      id
      name
      stateChangedAt
      submissionsPaused
      billingRegularRate
      createdAt
      createdBy {
        id
        name
      }
      organization {
        id
        name
      }
      canPending {
        value
      }

      canOpenUp {
        value
      }

      canHold {
        value
      }

      canCancel {
        value
      }
      code
      ...StaffingRequestCandidateList
      ...StaffingRequestBarFragment
      ...StaffingRequestApprovalChainsFragment
      staffingRequestApplications {
        id
        employmentType
        createdAt
        ...StaffingRequestApplicationProfileInfo
        ...StaffingRequestMakeOfferInfo
        createdBy {
          id
          name
          email
        }
        supplier {
          id
          name
        }
        canIncreaseOfferRate {
          value
        }
        staffingRequest {
          id
        }
        activeOffer {
          id
          state
          stateChangedAt
          rejectionReason
          rejectionDetails
          staffingRequestAssignment {
            id
          }
          history {
            ...HistoryInfoFragment
          }
          # FIXME: fetch id to help with apollo cache
          # eslint-disable-next-line @graphql-eslint/require-id-when-available
          ...OfferDetailsInfo
        }
      }
    }
  }
  ${CandidateTabs.fragments.candidateList}
  ${ApplicationProfile.fragments.profileInfo}
  ${OfferForm.fragments.staffingRequestInfo}
  ${StaffingRequestBar.fragment}
  ${ApprovalChainContentFragments.staffingRequest}
  ${HistoryInfoFragment}
  ${OFFER_INFO_DETAILS_FRAGMENT}
`;

export function StaffingRequestPage() {
  const { id: staffingRequestId } = useParams();

  const { loading, error, data } = useQuery(STAFFING_REQUEST_NAME, {
    variables: { id: staffingRequestId },
  });

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="StaffingRequestPage" />;
  }

  const { staffingRequest } = data;

  setBreadcrumbs([
    { name: 'Staffing', path: '/works/staffing_requests' },
    {
      name: staffingRequest.name,
      path: `/works/staffing_requests/${staffingRequestId}`,
    },
  ]);

  const CandidateTabsContent = () => (
    <Row gutter={[16, 16]} align="middle">
      <Col>
        <Title level={2} style={{ marginBottom: 0 }}>
          Candidates
        </Title>
      </Col>
      <Col>
        {staffingRequest && staffingRequest.state === 'OPEN' && (
          <SubmissionsPausedDropdown
            staffingRequestId={staffingRequest.id}
            canUpdate={staffingRequest.canUpdate.value}
            submissionsPaused={staffingRequest.submissionsPaused}
          />
        )}
      </Col>

      <Col xs={24}>
        <CandidateTabs staffingRequest={staffingRequest} />
      </Col>
    </Row>
  );

  return (
    <>
      <StaffingRequestBar
        showDropDown
        showState={false}
        staffingRequest={staffingRequest}
      />
      <AppContent>
        {staffingRequest.state === 'PENDING' &&
        staffingRequest.approvalChains?.length > 0 ? (
          <ApprovalChainDecisionSteps
            approvalChain={
              staffingRequest.approvalChains[
                staffingRequest.approvalChains.length - 1
              ]
            }
            displayActionButtonsInsideStep
            displayTitle
            extraApprovalButton={
              <EditStaffingRequestOnApprovalButton
                staffingRequestId={staffingRequestId}
                openPositionsCount={
                  staffingRequest.openStaffingRequestPositionsCount
                }
              />
            }
          />
        ) : (
          <CandidateTabsContent />
        )}
      </AppContent>
    </>
  );
}

function EditStaffingRequestOnApprovalButton({
  staffingRequestId,
  openPositionsCount,
}) {
  if (openPositionsCount <= 1) {
    return null;
  }

  return (
    <Link
      to={buildDialogUrl('edit-staffing-request-on-approval-drawer', {
        props: { staffingRequestId },
      })}
    >
      <Button size="large">Edit Open Positions</Button>
    </Link>
  );
}

EditStaffingRequestOnApprovalButton.propTypes = {
  staffingRequestId: PropTypes.string.isRequired,
  openPositionsCount: PropTypes.number.isRequired,
};

StaffingRequestPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};
